import React, { useState, useEffect } from "react";
import {
    FaSearch,
    FaTrash,
    FaAndroid,
    FaApple,
    FaUsers,
    FaExclamationTriangle,
    FaSync,
} from "react-icons/fa";
import { motion } from "framer-motion";
import ApiService from "../models/ApiService";

const Students = () => {
    // State for students data
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalUser, setTotalUser] = useState(0);
    const [andriodUser, setAndriodUser] = useState(0);
    const [iosUser, setIosUser] = useState(0);
    // State for filters and pagination
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState({
        key: "first_name",
        direction: "asc",
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [activeFilter, setActiveFilter] = useState("all"); // "all", "android", "ios"
    const studentsPerPage = 10;

    // API Base URL - replace with your actual API endpoint

    const fetchStudents = async () => {
        try {
            setLoading(true);
            setError(null);

            const res = await ApiService.GetAllUser();
            console.log("FULL API RESPONSE:", res);

            setTotalUser(res?.data?.responseContent?.totalUser || 0);
            setAndriodUser(res?.data?.responseContent?.totalAndroid || 0);
            setIosUser(res?.data?.responseContent?.totalIOS || 0);
            // ✅ Access the correct path for users
            const data = res?.data?.responseContent?.users || [];

            if (!Array.isArray(data)) {
                throw new Error("Invalid response format: users array not found");
            }

            // ✅ Transform API response
            const transformedStudents = data.map((user) => ({
                id: user._id,
                name: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
                email: user.email || "",
                phone: user.mobile || "",
                joinDate: user.createdAt
                    ? new Date(user.createdAt).toLocaleDateString()
                    : "",
                platform: user.platform || (Math.random() > 0.5 ? "Android" : "iOS"),
            }));

            setStudents(transformedStudents);
        } catch (err) {
            setError(err.message);
            console.error("Error fetching users:", err);
        } finally {
            setLoading(false);
        }
    };

    // Fetch students on component mount
    useEffect(() => {
        fetchStudents();
    }, []);

    // Calculate user statistics
    const totalUsers = students.length;
    const androidUsers = students.filter(
        (student) => student.platform === "android"
    ).length;
    const iosUsers = students.filter(
        (student) => student.platform === "ios"
    ).length;

    // Filter and sort students
    const filteredStudents = students
        .filter((student) => {
            const matchesSearch =
                student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.phone.includes(searchTerm);

            const matchesPlatform =
                activeFilter === "all" || student.platform === activeFilter;

            return matchesSearch && matchesPlatform;
        })
        .sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) {
                return sortConfig.direction === "asc" ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
                return sortConfig.direction === "asc" ? 1 : -1;
            }
            return 0;
        });

    // Pagination logic
    const indexOfLastStudent = currentPage * studentsPerPage;
    const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
    const currentStudents = filteredStudents.slice(
        indexOfFirstStudent,
        indexOfLastStudent
    );
    const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);

    // Handle sort
    const requestSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });
    };

    // Handle card click to filter data
    const handleCardClick = (filterType) => {
        setActiveFilter(filterType);
        setCurrentPage(1); // Reset to first page when filter changes
        setSearchTerm(""); // Clear search term when using card filter
    };

    // Clear all filters
    const clearFilters = () => {
        setActiveFilter("all");
        setSearchTerm("");
        setCurrentPage(1);
    };

    // Handle refresh data
    const handleRefresh = () => {
        fetchStudents();
        clearFilters();
    };

    const [visiblePages, setVisiblePages] = useState(5);

    useEffect(() => {
        // Adjust number of visible pages based on screen size
        const updateVisiblePages = () => {
            if (window.innerWidth < 640) setVisiblePages(2); // Mobile
            else if (window.innerWidth < 1024) setVisiblePages(3); // Tablet
            else setVisiblePages(5); // Desktop
        };

        updateVisiblePages();
        window.addEventListener("resize", updateVisiblePages);
        return () => window.removeEventListener("resize", updateVisiblePages);
    }, []);

    // Calculate which pages to show
    const half = Math.floor(visiblePages / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + visiblePages - 1);

    if (end - start + 1 < visiblePages && start > 1) {
        start = Math.max(1, end - visiblePages + 1);
    }

    const pageNumbers = Array.from(
        { length: end - start + 1 },
        (_, i) => start + i
    );
    // Loading state
    if (loading && students.length === 0) {
        return (
            <div className="container mx-auto p-4 flex justify-center items-center min-h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[--border-color-g] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading users...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error && students.length === 0) {
        return (
            <div className="container mx-auto p-4">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    <div className="flex items-center">
                        <FaExclamationTriangle className="mr-2" />
                        <span>Error: {error}</span>
                    </div>
                    <button
                        onClick={fetchStudents}
                        className="mt-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4">
            {/* Error Message */}
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <FaExclamationTriangle className="mr-2" />
                            <span>{error}</span>
                        </div>
                        <button
                            onClick={() => setError(null)}
                            className="text-red-700 hover:text-red-900"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {/* Total Users Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`rounded-lg shadow-lg p-6 cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-xl ${activeFilter === "all"
                        ? "bg-[--secondary-color]"
                        : "bg-[--primary-color]"
                        }`}
                    onClick={() => handleCardClick("all")}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-medium text-white">Total Users</p>
                            <p className="text-2xl font-bold text-white">{totalUser}</p>
                        </div>
                        <div className={`p-3 rounded-full ${activeFilter === "all" ? "bg-orange-100" : "bg-blue-100"
                            }`}>
                            <FaUsers className={`text-xl ${activeFilter === "all" ? "text-[--secondary-color]" : "text-blue-500"
                                }`} />
                        </div>
                    </div>
                    <div className="mt-2">
                        <span className="text-white">All registered users</span>
                    </div>

                </motion.div>

                {/* Android Users Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                    className={`rounded-lg shadow-lg p-6 cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-xl ${activeFilter === "Android"
                        ? "bg-[--secondary-color] "
                        : "bg-[--primary-color]"
                        }`}
                    onClick={() => handleCardClick("Android")}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-medium text-white">Android Users</p>
                            <p className="text-2xl font-bold text-white">{andriodUser}</p>
                        </div>
                        <div className={`p-3 rounded-full ${activeFilter === "Android" ? "bg-orange-100" : "bg-green-100"
                            }`}>
                            <FaAndroid className={`text-xl ${activeFilter === "Android" ? "text-[--secondary-color]" : "text-green-500"
                                }`} />
                        </div>
                    </div>
                    <div className="mt-2">
                        <span className="text-white">
                            {totalUser > 0 ? Math.round((andriodUser / totalUser) * 100) : 0}%
                            of total
                        </span>
                    </div>

                </motion.div>

                {/* iOS Users Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                    className={`rounded-lg shadow-lg p-6 cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-xl ${activeFilter === "iOS"
                        ? "bg-[--secondary-color] "
                        : "bg-[--primary-color]"
                        }`}
                    onClick={() => handleCardClick("iOS")}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-medium text-white">iOS Users</p>
                            <p className="text-2xl font-bold text-white">{iosUser}</p>
                        </div>
                        <div className={`p-3 rounded-full ${activeFilter === "iOS" ? "bg-orange-100" : "bg-gray-100"
                            }`}>
                            <FaApple className={`text-xl ${activeFilter === "iOS" ? "text-[--secondary-color]" : "text-black"
                                }`} />
                        </div>
                    </div>
                    <div className="mt-2">
                        <span className="text-white">
                            {totalUser > 0 ? Math.round((iosUser / totalUser) * 100) : 0}% of
                            total
                        </span>
                    </div>

                </motion.div>
            </div>

            {/* Active Filter Indicator and Clear Button */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-[--text-color]">User List</h2>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                    <div className="relative flex-grow">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaSearch className="text-gray-400" />
                        </div>
                        <input
                            type="text"
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            placeholder="Search students..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Student table */}
            <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-[--header-color-g]">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-white">
                                <div className="flex items-center uppercase">Name</div>
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left uppercase text-white"
                            >
                                Contact
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left uppercase text-white"
                            >
                                Platform
                            </th>
                            <th className="px-6 py-3 text-left text-white uppercase">
                                <div className="flex items-center uppercase">created at</div>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {currentStudents.length > 0 ? (
                            currentStudents.map((student) => (
                                <motion.tr
                                    key={student.id}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.3 }}
                                    className="hover:bg-gray-50"
                                >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                                                {student.name.charAt(0)}
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {student.name ? student.name : "N/A"}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {student.email ? student.email : "N/A"}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {student.phone ? student.phone : "-"}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            {student.platform === "Android" ? (
                                                <FaAndroid className="text-green-500 mr-2" />
                                            ) : (
                                                <FaApple className="text-gray-800 mr-2" />
                                            )}
                                            <span className="text-sm text-gray-900 capitalize">
                                                {student.platform}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {new Date(student.joinDate).toLocaleDateString()}
                                        </div>
                                    </td>
                                </motion.tr>
                            ))
                        ) : (
                            <tr>
                                <td
                                    colSpan="5"
                                    className="px-6 py-4 text-center text-sm text-gray-500"
                                >
                                    {searchTerm || activeFilter !== "all"
                                        ? "No users found matching your criteria"
                                        : "No students found"}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Loading overlay for table */}
            {loading && students.length > 0 && (
                <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
            )}

            {/* Pagination */}
            {filteredStudents.length > studentsPerPage && (
                <div className="flex items-center justify-between mt-4 mb-2 px-3 py-3 bg-gray-50 border-t border-gray-200 sm:px-6">
                    {/* Mobile controls */}
                    <div className="flex-1 flex justify-between sm:hidden">
                        <button
                            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                            disabled={currentPage === 1 || loading}
                            className="px-4 py-2 border border-gray-300 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-100 disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                            disabled={currentPage === totalPages || loading}
                            className="px-4 py-2 border border-gray-300 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-100 disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>

                    {/* Desktop/tablet pagination */}
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                        <p className="text-sm text-gray-700">
                            Showing{" "}
                            <span className="font-medium">{indexOfFirstStudent + 1}</span> to{" "}
                            <span className="font-medium">
                                {Math.min(indexOfLastStudent, filteredStudents.length)}
                            </span>{" "}
                            of <span className="font-medium">{filteredStudents.length}</span>{" "}
                            results
                            {activeFilter !== "all" && (
                                <span className="text-blue-600 ml-1">
                                    ({activeFilter === "Android" ? "Android" : "iOS"} users)
                                </span>
                            )}
                        </p>

                        <nav
                            className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                            aria-label="Pagination"
                        >
                            <button
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1 || loading}
                                className="px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                            >
                                «
                            </button>

                            <button
                                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                disabled={currentPage === 1 || loading}
                                className="px-2 py-2 border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                            >
                                ‹
                            </button>

                            {pageNumbers.map((page) => (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    disabled={loading}
                                    className={`px-4 py-2 border text-sm font-medium ${currentPage === page
                                        ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                        : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                                        } disabled:opacity-50`}
                                >
                                    {page}
                                </button>
                            ))}

                            <button
                                onClick={() =>
                                    setCurrentPage((p) => Math.min(p + 1, totalPages))
                                }
                                disabled={currentPage === totalPages || loading}
                                className="px-2 py-2 border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                            >
                                ›
                            </button>

                            <button
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage === totalPages || loading}
                                className="px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                            >
                                »
                            </button>
                        </nav>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Students;
